/**
 * Conjuntos de uma campanha, cada um expansível para os anúncios dele.
 *
 * Os anúncios só são buscados quando o conjunto é aberto: uma campanha com 10
 * conjuntos faria 20 chamadas à Meta de uma vez se fosse tudo de cara.
 */

import { useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, ChevronRight, ExternalLink, Loader2 } from "lucide-react";
import { moeda, numero, rotuloResultado, statusDe, OTIMIZACAO, type Periodo } from "@/lib/meta-ads-format";
import { StatusToggle } from "./StatusToggle";
import { RowActionsMenu } from "./RowActionsMenu";
import { BudgetDialog } from "./BudgetDialog";

type Props = {
  campaignId: string;
  adAccountId: string | null | undefined;
  currency: string | null;
  periodo: Periodo;
  canManage: boolean;
  canActivate: boolean;
  contaBloqueada: boolean;
};

export function AdsetsTable({ campaignId, adAccountId, currency, periodo, canManage, canActivate, contaBloqueada }: Props) {
  const utils = trpc.useUtils();
  const [aberto, setAberto] = useState<Set<string>>(new Set());
  const [editandoVerba, setEditandoVerba] = useState<{ id: string; nome: string; daily: number | null; total: boolean } | null>(null);

  const conjuntos = trpc.metaAds.adsets.list.useQuery({ campaignId, limit: 50 }, { staleTime: 30_000 });
  const metricas = trpc.metaAds.insights.get.useQuery(
    { level: "adset", objectId: campaignId, datePreset: periodo, limit: 100 },
    { staleTime: 60_000 },
  );
  const porConjunto = useMemo(() => {
    const mapa = new Map<string, NonNullable<typeof metricas.data>["items"][number]>();
    for (const l of metricas.data?.items ?? []) if (l.adsetId) mapa.set(String(l.adsetId), l);
    return mapa;
  }, [metricas.data]);

  const invalidar = () => {
    void utils.metaAds.adsets.list.invalidate();
    void utils.metaAds.ads.list.invalidate();
  };
  const alternar = (id: string) =>
    setAberto((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <Card>
      <CardContent className="pt-6">
        <h2 className="mb-3 font-medium">Conjuntos de anúncios</h2>
        {conjuntos.isLoading && <p className="text-sm text-muted-foreground">Carregando conjuntos…</p>}
        {conjuntos.isError && <p className="text-sm text-red-600">{toUserFriendlyError(conjuntos.error)}</p>}
        {conjuntos.data?.items.length === 0 && <p className="text-sm text-muted-foreground">Esta campanha ainda não tem conjuntos.</p>}

        {(conjuntos.data?.items.length ?? 0) > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="w-8 pb-2"></th>
                  <th className="pb-2 pr-3 font-medium">Conjunto</th>
                  <th className="pb-2 pr-3 font-medium">Status</th>
                  <th className="pb-2 pr-3 font-medium">Otimização</th>
                  <th className="pb-2 pr-3 text-right font-medium">Verba/dia</th>
                  <th className="pb-2 pr-3 text-right font-medium">Gasto</th>
                  <th className="pb-2 pr-3 text-right font-medium">Cliques</th>
                  <th className="pb-2 pr-3 text-right font-medium">Resultados</th>
                  <th className="pb-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {conjuntos.data?.items.map((a) => {
                  const m = porConjunto.get(String(a.id));
                  const st = statusDe(a.effectiveStatus);
                  const resultado = m?.resultado ?? null;
                  const expandido = aberto.has(a.id);
                  return (
                    <AdsetRowGroup key={a.id} expandido={expandido}>
                      <tr className="border-b">
                        <td className="py-2">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => alternar(a.id)} aria-label={expandido ? "Recolher" : "Ver anúncios"}>
                            {expandido ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </Button>
                        </td>
                        <td className="max-w-[360px] py-2 pr-3">
                          <button type="button" className="line-clamp-2 text-left font-medium hover:underline" onClick={() => alternar(a.id)}>
                            {a.name}
                          </button>
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            {canActivate && !contaBloqueada && (
                              <StatusToggle objectId={a.id} objectType="adset" status={a.status} effectiveStatus={a.effectiveStatus} dailyBudgetCents={a.dailyBudgetCents} currency={currency} onChanged={invalidar} />
                            )}
                            <Badge className={st.className}>{st.label}</Badge>
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-muted-foreground">{OTIMIZACAO[a.optimizationGoal ?? ""] ?? a.optimizationGoal ?? "—"}</td>
                        <td className="py-2 pr-3 text-right">
                          {a.dailyBudgetCents != null
                            ? moeda(a.dailyBudgetCents, currency)
                            : a.lifetimeBudgetCents != null
                              ? <span title="Verba total do período">{moeda(a.lifetimeBudgetCents, currency)} total</span>
                              : <span className="text-muted-foreground" title="Verba definida na campanha">na campanha</span>}
                        </td>
                        <td className="py-2 pr-3 text-right">{moeda(m?.spendCents, currency)}</td>
                        <td className="py-2 pr-3 text-right">{numero(m?.clicks)}</td>
                        <td className="py-2 pr-3 text-right">
                          {numero(resultado?.value)}
                          {resultado && <span className="ml-1 text-xs text-muted-foreground">{rotuloResultado(resultado.type)}</span>}
                        </td>
                        <td className="py-2 text-right">
                          <RowActionsMenu
                            objectType="adset"
                            objectId={a.id}
                            nome={a.name}
                            adAccountId={adAccountId}
                            canManage={canManage}
                            canActivate={canActivate}
                            contaBloqueada={contaBloqueada}
                            onEditBudget={() => setEditandoVerba({ id: a.id, nome: a.name, daily: a.dailyBudgetCents, total: a.dailyBudgetCents == null && a.lifetimeBudgetCents != null })}
                            onDuplicated={invalidar}
                          />
                        </td>
                      </tr>
                      {expandido && (
                        <tr className="border-b bg-muted/30">
                          <td></td>
                          <td colSpan={8} className="py-2 pr-2">
                            <AdsRows adsetId={a.id} adAccountId={adAccountId} currency={currency} periodo={periodo} canManage={canManage} canActivate={canActivate} contaBloqueada={contaBloqueada} />
                          </td>
                        </tr>
                      )}
                    </AdsetRowGroup>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {conjuntos.data?.nextCursor && (
          <p className="mt-3 text-xs text-muted-foreground">Mostrando os primeiros {conjuntos.data.items.length} conjuntos.</p>
        )}
      </CardContent>

      {editandoVerba && (
        <BudgetDialog
          open
          onOpenChange={(o) => !o && setEditandoVerba(null)}
          objectType="adset"
          objectId={editandoVerba.id}
          nome={editandoVerba.nome}
          currentDailyCents={editandoVerba.daily}
          usaVerbaTotal={editandoVerba.total}
          currency={currency}
          onSaved={invalidar}
        />
      )}
    </Card>
  );
}

/** Só agrupa as duas <tr> de um conjunto com uma key. */
function AdsetRowGroup({ children }: { expandido: boolean; children: React.ReactNode }) {
  return <>{children}</>;
}

function AdsRows({ adsetId, adAccountId, currency, periodo, canManage, canActivate, contaBloqueada }: Omit<Props, "campaignId"> & { adsetId: string }) {
  const utils = trpc.useUtils();
  const anuncios = trpc.metaAds.ads.list.useQuery({ adsetId, limit: 50 }, { staleTime: 30_000 });
  const metricas = trpc.metaAds.insights.get.useQuery(
    { level: "ad", objectId: adsetId, datePreset: periodo, limit: 100 },
    { staleTime: 60_000 },
  );
  const porAnuncio = useMemo(() => {
    const mapa = new Map<string, NonNullable<typeof metricas.data>["items"][number]>();
    for (const l of metricas.data?.items ?? []) if (l.adId) mapa.set(String(l.adId), l);
    return mapa;
  }, [metricas.data]);
  const invalidar = () => void utils.metaAds.ads.list.invalidate();

  if (anuncios.isLoading) {
    return (
      <p className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Carregando anúncios…
      </p>
    );
  }
  if (anuncios.isError) return <p className="py-1 text-xs text-red-600">{toUserFriendlyError(anuncios.error)}</p>;
  if (anuncios.data?.items.length === 0) return <p className="py-1 text-xs text-muted-foreground">Nenhum anúncio neste conjunto.</p>;

  return (
    <table className="w-full text-sm">
      <tbody>
        {anuncios.data?.items.map((ad) => {
          const m = porAnuncio.get(String(ad.id));
          const st = statusDe(ad.effectiveStatus);
          const resultado = m?.resultado ?? null;
          return (
            <tr key={ad.id} className="border-b border-dashed last:border-0">
              <td className="w-12 py-1.5 pr-2">
                {ad.creative?.thumbnailUrl ? (
                  <img src={ad.creative.thumbnailUrl} alt="" className="h-9 w-9 rounded object-cover" />
                ) : (
                  <div className="h-9 w-9 rounded bg-muted" />
                )}
              </td>
              <td className="max-w-[320px] py-1.5 pr-3">
                <p className="line-clamp-1 font-medium">{ad.name}</p>
                {ad.previewLink && (
                  <a href={ad.previewLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                    <ExternalLink className="h-3 w-3" /> Ver prévia
                  </a>
                )}
              </td>
              <td className="py-1.5 pr-3">
                <div className="flex items-center gap-2">
                  {canActivate && !contaBloqueada && (
                    <StatusToggle objectId={ad.id} objectType="ad" status={ad.status} effectiveStatus={ad.effectiveStatus} dailyBudgetCents={null} currency={currency} onChanged={invalidar} />
                  )}
                  <Badge className={st.className}>{st.label}</Badge>
                </div>
              </td>
              <td className="py-1.5 pr-3 text-right">{moeda(m?.spendCents, currency)}</td>
              <td className="py-1.5 pr-3 text-right">{numero(m?.clicks)} cliques</td>
              <td className="py-1.5 pr-3 text-right">
                {numero(resultado?.value)}
                {resultado && <span className="ml-1 text-xs text-muted-foreground">{rotuloResultado(resultado.type)}</span>}
              </td>
              <td className="py-1.5 text-right">
                <RowActionsMenu objectType="ad" objectId={ad.id} nome={ad.name} adAccountId={adAccountId} canManage={canManage} canActivate={canActivate} contaBloqueada={contaBloqueada} onDuplicated={invalidar} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
